import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogoWhite } from '../../components/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, LogIn, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success('Bienvenido a ENTRICODE');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Gradient background (replaced external CDN image to remove third-party dependency) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-950/60 to-blue-900/40" />
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <Link to="/">
            <LogoWhite size="md" />
          </Link>
          <div>
            <h2 className="text-3xl font-bold text-white leading-tight mb-3">
              Gestiona tus eventos<br />como un profesional
            </h2>
            <p className="text-slate-300 text-sm max-w-sm">
              Registro, QR, credenciales, control de accesos y modo offline. Todo desde un solo panel.
            </p>
          </div>
        </div>
      </div>

      {/* Right: Form */}
      <div className="w-full lg:w-1/2 bg-slate-950 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <Link to="/">
              <LogoWhite size="md" />
            </Link>
          </div>

          <Link to="/" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-300 text-sm mb-8 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Volver al inicio
          </Link>

          <h1 className="text-2xl font-bold text-white mb-1">Iniciar sesion</h1>
          <p className="text-slate-400 text-sm mb-8">Accede al panel de administracion</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-card">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Email</Label>
              <Input
                type="email"
                placeholder="admin@ejemplo.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-sm">Contrasena</Label>
              <Input
                type="password"
                placeholder="********"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-500 h-11 font-semibold"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Iniciando sesion...</>
              ) : (
                <><LogIn className="w-4 h-4 mr-2" /> Iniciar sesion</>
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500">
            No tienes cuenta?{' '}
            <Link to="/admin/register" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="register-link">
              Registrate aqui
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
